import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import {TableColumnsStyle, TableColumnExpansion, FirstColumnStyle, FlexColumnStyle, FirstColumnFlexStyle} from './Table.styled';
import {CaretIcon} from '../../icons/caret-icon';
import {TableGrouping} from './';
import {LayoutContext} from './table';

export const TableColumns = React.memo(function TableColumns({
	children,
	level,
	canExpand,
	canExpandRow,
	expanded,
	isVisible,
	toggleExpansion,
	isHeader,
	isLastRow,
	fixedHeight,
}) {
	const tableLayout = useContext(LayoutContext);
	const handleOnClick = canExpandRow ? toggleExpansion : undefined;

	const getChildren = () => {
		const groupedChildren = [];

		React.Children.forEach(children, (child, index) => {
			if (!tableLayout.columns[index]?.visible) {
				return;
			}

			const cloneProps = {
				...tableLayout.columns[index],
				usePadding: child.props.usePadding || tableLayout.columns[index].usePadding,
				align: child.props.align || tableLayout.columns[index].align,
				key: `column-${index}`,
			};
			const clonedChild = React.cloneElement(child, cloneProps);
			if (tableLayout.groupings[index] === null) {
				groupedChildren.push({
					children: [
						getWrapper(index, tableLayout, isHeader, level, expanded, canExpand, canExpandRow, child, clonedChild),
					],
					group: null,
				});
			} else {
				const lastIndex = groupedChildren.length - 1;
				if (!!groupedChildren[lastIndex] && groupedChildren[lastIndex].group === tableLayout.groupings[index]) {
					groupedChildren[lastIndex].children.push(
						getWrapper(index, tableLayout, isHeader, level, expanded, canExpand, canExpandRow, child, clonedChild)
					);
				} else {
					groupedChildren.push({
						children: [
							getWrapper(
								index,
								tableLayout,
								isHeader,
								level,
								expanded,
								canExpand,
								canExpandRow,
								child,
								clonedChild
							),
						],
						group: tableLayout.groupings[index],
						minGroupingWidth: tableLayout.minGroupingWidths[index],
					});
				}
			}
		});

		// Wrapped grouped children in a TableGrouping
		return groupedChildren.map((groupObject, index, array) => {
			if (groupObject.group !== null) {
				let lastGrouping = index !== array.length - 1 && !array[index + 1].group;

				return (
					<TableGrouping
						key={`position-${groupObject.group}`}
						lastGrouping={lastGrouping}
						minWidth={groupObject.minGroupingWidth}
					>
						{groupObject.children}
					</TableGrouping>
				);
			} else {
				return groupObject.children;
			}
		});
	};

	return (
		<TableColumnsStyle
			isLastRow={isLastRow}
			level={level}
			canExpand={canExpandRow}
			onClick={handleOnClick}
			fixedHeight={fixedHeight}
		>
			{isVisible && getChildren()}
		</TableColumnsStyle>
	);
});

TableColumns.propTypes = {
	children: PropTypes.node,
	level: PropTypes.oneOf([1, 2, 3, 4, 5]),
	canExpand: PropTypes.bool,
	expanded: PropTypes.bool,
	isVisible: PropTypes.bool.isRequired,
	toggleExpansion: PropTypes.func,
	isHeader: PropTypes.bool,
	isLastRow: PropTypes.bool,
	canExpandRow: PropTypes.bool,
	fixedHeight: PropTypes.number,
};
function getWrapper(index, tableLayout, isHeader, level, expanded, canExpand, canExpandRow, child, clonedChild) {
	let caretComponent = null;
	if (canExpandRow) {
		caretComponent = <CaretIcon color="#2A2A37" direction={expanded ? CaretIcon.DIRECTION.UP : CaretIcon.DIRECTION.DOWN} />;
	} else if (canExpand) {
		caretComponent = <div style={{width: '12px'}} />;
	}
	if (index === 0 && tableLayout.columns[index].flex) {
		return (
			<FirstColumnFlexStyle
				key={`wrapper-${index}`}
				level={level}
				flex={tableLayout.columns[index].flex}
				isHeader={isHeader}
				width={tableLayout.columns[index].width}
			>
				<TableColumnExpansion level={level} canExpand={canExpand}>
					{caretComponent}
				</TableColumnExpansion>
				{React.cloneElement(child, {
					canExpand,
					wrapped: true,
					level,
					width: tableLayout.columns[index].width,
					usePadding: tableLayout.columns[index].usePadding,
				})}
			</FirstColumnFlexStyle>
		);
	} else if (index === 0) {
		return (
			<FirstColumnStyle
				key={`wrapper-${index}`}
				align={tableLayout.columns[index].align}
				isHeader={isHeader}
				width={tableLayout.columns[index].width}
			>
				<TableColumnExpansion level={level} canExpand={canExpand}>
					{caretComponent}
				</TableColumnExpansion>
				{React.cloneElement(child, {
					canExpand,
					wrapped: true,
					level,
					width: tableLayout.columns[index].width,
					usePadding: tableLayout.columns[index].usePadding,
				})}
			</FirstColumnStyle>
		);
	} else if (tableLayout.columns[index].flex) {
		return (
			<FlexColumnStyle
				key={`wrapper-${index}`}
				align={tableLayout.columns[index].align}
				flex={tableLayout.columns[index].flex}
				width={tableLayout.columns[index].width}
			>
				{React.cloneElement(clonedChild, {wrapped: true})}
			</FlexColumnStyle>
		);
	} else {
		return clonedChild;
	}
}
